<template>
  <div v-if="$store.state.auth.user.user_type == 'user'" class="font-medium text-xl">
    {{ $t("Team Collaborators") }}
  </div>

  <div v-if="$store.state.auth.user.user_type == 'user'" class="mt-8">
    <div class="text-common-grey mb-4">
      {{ $t("Select your team members to share this workspace with") }}
    </div>
    <div class="overflow-visible">
      <div v-for="team_member in workspace.team_members" :key="team_member.id" class="my-8">
        <div class="flex">
          <div class="flex space-x-5 w-[500px] items-center">
            <img v-if="team_member.avatar" class="inline-block rounded-full h-9 w-9 object-cover" :src="team_member.avatar" alt="">
            <div v-else class="relative">
              <Avatar :radius="40" :hover-effect="true" :first-name="team_member.first_name" :last-name="team_member.last_name" />
              <div
                v-if="workspace.created_by !== team_member.id"
                id="remove-user-button"
                class="cursor-pointer border bg-light-screen-background-grey dark:bg-dark-screen-background dark:border-dark-divider-border text-red-400 absolute w-4 h-4 rounded-full z-1 -top-2 -right-1 text-xs"
                @click="handleRemoveTeamMember(team_member)">
                <span class="ml-[4px] relative bottom-[2px]">x</span>
              </div>
            </div>
            <div class="ml-7">
              {{ team_member.full_name }}
            </div>
          </div>
          <div>
            <div v-if="team_member.workspace_access.status == 'pending'" class="italic">
              {{ $t("Invitation Sent") }}
            </div>
            <div v-else-if="team_member.workspace_access.status == 'expired'" class="italic">
              {{ $t("Invitation Expired") }}. <span class="text-light-brand-blue cursor-pointer">{{ $t("Send Again?") }}</span>
            </div>

            <SelectInput
              v-else
              :disabled="workspace.created_by == team_member.id"
              :value="team_member.workspace_access.access_scope"
              :is-box-background="true"
              :allow-null="false"
              value-attr="value"
              label-attr="label"
              name="tag"
              :items="team_permission_selections"
              :placeholder="$t('Permission')"
              :label="$t('Permission')"
              :show-label="true"
              class="w-44 font-normal"
              input-classes="appearance-none text-left block w-full w-[100px] border border-gray-300 rounded-md shadow-sm placeholder-gray-400 font-normal focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              @update:currentItem="
                ($event) => {
                  handleChangeTeamMemberPermission($event, team_member)
                }
              " />
          </div>
        </div>
      </div>
    </div>
    <button
      class="btn cancel-btn border-light-brand-blue text-light-brand-blue"
      @click="$emitter.emit(ALL_EMITTERS.ADD_NEW_WORKSPACE_TEAM_COLLABORATOR, workspace)">
      <Plus class="w-5 h-5 ml-0 mr-2" /> {{ $t("Add new team collaborator") }}
    </button>
  </div>

  <div class="font-medium text-xl mt-6">
    {{ $t("Contact Collaborators") }}
  </div>
  <div class="mt-8">
    <div class="text-common-grey mb-4">
      {{ $t("Select collaborators to share this workspace with") }}
    </div>
    <div class="overflow-visible">
      <div v-for="collaborator in workspace.contacts" :key="collaborator.id" class="my-8">
        <div class="flex">
          <div class="flex space-x-5 w-[500px] items-center">
            <img v-if="collaborator.avatar" class="inline-block rounded-full h-9 w-9 object-cover" :src="collaborator.avatar" alt="">

            <div v-else class="relative">
              <Avatar :radius="40" :hover-effect="true" :first-name="collaborator.first_name" :last-name="collaborator.last_name" />
              <div
                id="remove-user-button"
                class="cursor-pointer border bg-light-screen-background-grey dark:bg-dark-screen-background dark:border-dark-divider-border text-red-400 absolute w-4 h-4 rounded-full z-1 -top-2 -right-1 text-xs"
                @click="handleRemoveCollaborator(collaborator)">
                <span class="ml-[4px] relative bottom-[2px]">x</span>
              </div>
            </div>
            <div class="ml-7">
              {{ collaborator.first_name }} {{ collaborator.last_name }}
            </div>
          </div>
          <div>
            <div v-if="collaborator.workspace_access?.status == 'pending'" class="italic">
              {{ $t("Invitation Sent") }}
            </div>
            <div v-else-if="collaborator.workspace_access?.status == 'expired'" class="italic">
              {{ $t("Invitation Expired") }}. <span class="text-light-brand-blue cursor-pointer">{{ $t("Send Again?") }}</span>
            </div>
            <SelectInput
              v-else
              :value="collaborator.workspace_access.access_scope"
              :is-box-background="true"
              :allow-null="false"
              name="tag"
              value-attr="value"
              label-attr="label"
              :items="permission_selections"
              :placeholder="$t('Permission')"
              :label="$t('Permission')"
              :show-label="true"
              class="w-44 font-normal"
              input-classes="appearance-none text-left block w-full w-[100px] border border-gray-300 rounded-md shadow-sm placeholder-gray-400 font-normal focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              @update:currentItem="
                ($event) => {
                  handleChangeCollaboratorPermission($event, collaborator)
                }
              " />
          </div>
        </div>
      </div>
    </div>
    <button
      class="btn cancel-btn border-light-brand-blue text-light-brand-blue -ml-1"
      @click="$emitter.emit(ALL_EMITTERS.ADD_NEW_WORKSPACE_CONTACT_COLLABORATOR, workspace)">
      <Plus class="w-5 h-5 ml-0 mr-2" /> {{ $t("Add new collaborator") }}
    </button>

    <div class="mt-20 w-96">
      <button
        class="btn primary-btn"
        @click="$router.push(`/workspace/${workspace.short_id}/${workspace.layout.filter((l) => l.is_visible)[0].id}`)">
        {{ $t("Go to Workspace") }}
      </button>
    </div>
  </div>
</template>

<script>
import Avatar from "@/components/shared/Avatar.vue"
import { Plus } from "lucide-vue-next"
import SelectInput from "@/components/shared/SelectInput.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: { Avatar, Plus, SelectInput },
  props: { workspace: null },
  emits: [],
  data() {
    return {
      ALL_EMITTERS,
      permission_selections: [
        { label: this.$t("Viewer"), value: "read" },
        { label: this.$t("Editor"), value: "write" },
        { label: this.$t("Admin"), value: "admin" }
      ],
      team_permission_selections: [
        { label: this.$t("Viewer"), value: "read" },
        { label: this.$t("Editor"), value: "write" },
        { label: this.$t("Admin"), value: "admin" }
      ]
    }
  },

  methods: {
    handleChangeTeamMemberPermission(e, team_member) {
      this.$store.dispatch("data/updateWorkspaceTeamMemberAccessScope", {
        short_id: this.workspace.short_id,
        access_scope: e,
        user: team_member.id
      })
    },
    handleChangeCollaboratorPermission(e, contact) {
      this.$store.dispatch("data/updateWorkspaceContactAccessScope", {
        short_id: this.workspace.short_id,
        access_scope: e,
        contact: contact.id
      })
    },
    handleRemoveTeamMember(team_member) {
      this.$emitter.emit(ALL_EMITTERS.REMOVE_WORKSPACE_TEAM_MEMBER, { short_id: this.workspace.short_id, user: team_member.id })
    },
    handleRemoveCollaborator(collaborator) {
      this.$emitter.emit(ALL_EMITTERS.REMOVE_WORKSPACE_CONTACT, { short_id: this.workspace.short_id, user: collaborator.id })
    }
  }
}
</script>
